/**
 * OfflineBanner Component
 * 
 * Displays a banner when the user is offline to inform them about connectivity issues.
 * Automatically hides when connection is restored.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOfflineDetection } from '@/shared/hooks';
import './OfflineBanner.css';

const OfflineBanner = () => {
  const { t } = useTranslation('common');
  const { isOnline } = useOfflineDetection();
  const [showBanner, setShowBanner] = useState(false);
  const prevOnlineRef = useRef(isOnline);
  const reconnectionTimerRef = useRef(null);

  useEffect(() => {
    // Manage body class for padding
    const body = document.body;
    
    // Clear any existing timer
    if (reconnectionTimerRef.current) {
      clearTimeout(reconnectionTimerRef.current);
      reconnectionTimerRef.current = null;
    }

    if (!isOnline) {
      // User went offline
      setShowBanner(true);
      body.classList.add('offline-banner-active');
      prevOnlineRef.current = false;
    } else if (isOnline && prevOnlineRef.current === false) {
      // User just came back online (transition from offline to online)
      setShowBanner(true);
      body.classList.add('offline-banner-active');
      prevOnlineRef.current = true;
      
      // Hide banner after 3 seconds with fade-out animation
      reconnectionTimerRef.current = setTimeout(() => {
        // Remove body class first to allow proper layout update
        body.classList.remove('offline-banner-active');
        
        // Add fade-out animation before removing
        const bannerElement = document.querySelector('.offline-banner');
        if (bannerElement) {
          bannerElement.style.animation = 'slideUp 0.3s ease-out forwards';
          setTimeout(() => {
            setShowBanner(false);
            reconnectionTimerRef.current = null;
            // Ensure body class is removed
            body.classList.remove('offline-banner-active');
          }, 300); // Wait for animation to complete
        } else {
          setShowBanner(false);
          body.classList.remove('offline-banner-active');
          reconnectionTimerRef.current = null;
        }
      }, 3000);
    } else if (isOnline && prevOnlineRef.current === true) {
      // Already online and staying online - don't show banner
      setShowBanner(false);
      body.classList.remove('offline-banner-active');
    }

    // Cleanup on unmount
    return () => {
      if (reconnectionTimerRef.current) {
        clearTimeout(reconnectionTimerRef.current);
        reconnectionTimerRef.current = null;
      }
      // Ensure body class is cleaned up
      body.classList.remove('offline-banner-active');
    };
  }, [isOnline]);

  if (!showBanner) {
    return null;
  }

  return (
    <div 
      className={`offline-banner ${isOnline ? 'offline-banner--online' : 'offline-banner--offline'}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="offline-banner__content">
        <span className="offline-banner__icon" aria-hidden="true">
          {isOnline ? '✓' : '⚠'}
        </span>
        <span className="offline-banner__message">
          {isOnline 
            ? t('offline.connectionRestored', { defaultValue: 'Connection restored. You are back online.' })
            : t('offline.currentlyOffline', { defaultValue: 'You are currently offline. Please check your internet connection.' })}
        </span>
      </div>
    </div>
  );
};

export default OfflineBanner;

